import queryString from 'query-string';

import { createAxiosInstance } from '@/plugins/axios.plugin';

const { PLATAFORMA_API } = process.env;

export class ContentService {
  static SERVICE_NAME = 'content';

  constructor() {
    this.client = createAxiosInstance({
      baseURL        : PLATAFORMA_API,
      useAccessToken : true
    });
  }

  getMenu(contentId) {
    return this.client.get(`/content/main-content/${contentId}/menu`);
  }

  createContent({ academicMainId, templateName }) {
    return this.client.post('/content/main-content/', { academicMainId, templateName });
  }

  createTopic(contentId, data) {
    return this.client.post(`/content/main-content/${contentId}/topics`, data);
  }

  deleteTopic(contentId, topic, data) {
    return this.client.put(`/content/main-content/${contentId}/topics/${topic.id}/delete`, data);
  }

  getWebconferenceProvider() {
    return this.client.get('/content/webconference/provider');
  }

  getCollabURL({ sessionId, name, email, providerId }) {
    const stringified = queryString.stringify({ name, email, providerId }, { skipNull: true, skipEmptyString: true });
    return this.client.get(`/content/webconference/collab/${sessionId}/url${stringified && `?${stringified}`}`);
  }

  getCollabRecords({ contextId }) {
    return this.client.get(`/content/webconference/collab/${contextId}/records`);
  }

  getCollabRecordURL({ recordId }) {
    return this.client.get(`/content/webconference/collab/record/${recordId}/url`);
  }

  reintegrateWebconference(topicId, data) {
    return this.client.put(`/content/webconference/reintegrate/${topicId}`, data);
  }

  getContentByTopicId(contentId, topicId) {
    return this.client.get(`/content/main-content/${contentId}/topics/${topicId}`);
  }

  patchContentHtmlByTopicId(params) {
    return this.client.patch(
      `/content/main-content/${params.contentId}/topic/${params.topicId}/content`,
      params.content
    );
  }

  updateTopic(contentId, data) {
    return this.client.put(`/content/main-content/${contentId}/topics/${data.id}`, data);
  }

  patchTopic(contentId, topicId, data) {
    return this.client.patch(`/content/main-content/${contentId}/topics/${topicId}`, data);
  }

  getFile(url) {
    return this.client.get(url, { responseType: 'blob' });
  }

  getTopicsOfPageByPageId({ contentId, topicId }) {
    return this.client.get(`content/main-content/${contentId}/page/${topicId}`);
  }

  createPageWithChildrens({ contentId, data }) {
    return this.client.post(`content/main-content/${contentId}/page/`, data);
  }

  getPostsByTopicId(topicId, contentId) {
    return this.client.get(`content/main-content/${contentId}/topic/${topicId}/post`);
  }

  postCommentInForum(topicId, enrollmentId, params) {
    return this.client.post(`content/topic/${topicId}/enrollment/${enrollmentId}/post`, params);
  }

  likeTopic(topicId, enrollmentId) {
    return this.client.post(`content/topic/${topicId}/enrollment/${enrollmentId}/like`);
  }

  dislikeTopic(topicId, enrollmentId) {
    return this.client.delete(`content/topic/${topicId}/enrollment/${enrollmentId}/like`);
  }

  likeComment(topicId, postId, enrollmentId) {
    return this.client.post(`content/topic/${topicId}/post/${postId}/enrollment/${enrollmentId}/like`);
  }

  dislikeComment(topicId, postId, enrollmentId) {
    return this.client.delete(`content/topic/${topicId}/post/${postId}/enrollment/${enrollmentId}/like`);
  }

  patchCommentOfTopic(topicId, postId, enrollmentId, data) {
    return this.client.patch(`content/topic/${topicId}/post/${postId}/enrollment/${enrollmentId}`, data);
  }

  createQuestion({ contentId, topicId, data }) {
    return this.client.post(`content/main-content/${contentId}/topics/${topicId}/questions`, data);
  }

  updateQuestion({ contentId, topicId, data }) {
    return this.client.put(`content/main-content/${contentId}/topics/${topicId}/questions/${data.id}`, data);
  }

  deleteQuestion({ contentId, topicId, questionId }) {
    return this.client.delete(`content/main-content/${contentId}/topics/${topicId}/questions/${questionId}`);
  }

  createQuiz({ contentId, params }) {
    return this.client.post(`content/main-content/${contentId}/quiz`, params);
  }

  reorderTopics({ contentId, topicId, data }) {
    return this.client.patch(`content/main-content/${contentId}/reorder/topics/${topicId}`, data);
  }

  getCorrectionsList(params) {
    return this.client.get(`content/main-content/${params.contentId}/corrections`, { params: params.queryParams });
  }

  getForumCorrectionContext(contentId, topicId, isToHideEnrollments) {
    return this.client.get(
      `content/main-content/${contentId}/corrections/${topicId}?isToHideEnrollments=${isToHideEnrollments}`
    );
  }

  getAnswersByEnrollmentIdAndTopicId(contentId, correctionId, enrollmentId) {
    return this.client.get(`content/main-content/${contentId}/corrections/${correctionId}/enrollment/${enrollmentId}`);
  }

  copyContent(data) {
    return this.client.post('content/main-content/copy', data);
  }

  processGrade(topicId) {
    return this.client.post(`content/topic/${topicId}/correction`);
  }

  changeMultipleGrades(params) {
    return this.client.put(`content/main-content/${params.contentId}/topics/${params.topicId}/questions`, {
      version   : params.version,
      questions : params.questions,
      grade     : params.grade
    });
  }

  getContextLtiByUrl(url) {
    return this.client.get(`content/link/lti/style?url=${url}`);
  }

  printContent(params) {
    return this.client.post(`content/main-content/${params.academicMainId}/topic/${params.topicId}/print`, {
      primaryColor : params.primaryColor,
      logoUrl      : params.logoUrl,
      language     : params.language
    });
  }

  createTopicEnrollmentException(params) {
    return this.client.post(`content/main-content/${params.contentId}/topic/${params.topicId}/enrollments-exceptions`, {
      enrollmentId   : params.enrollmentId,
      mainGroupId    : params.mainGroupId,
      deadlineAt     : params.deadlineAt,
      attemptsNumber : params.attemptsNumber
    });
  }
}

export default new ContentService();
